import { defineStore } from 'pinia';
import { ref } from 'vue';
import { doc, getDoc } from 'firebase/firestore';
import { metadataCollection, STORAGE_KEYS } from '../firebase/schema';

export const useCacheStore = defineStore('cache', () => {
  const lastSync = ref<Date | null>(null);
  const isOutdated = ref(false);

  // Cargar datos del caché
  const loadFromCache = <T>(key: string): T | null => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading cache for ' + key + ':', error);
      return null;
    }
  };

  // Guardar datos en caché
  const saveToCache = <T>(key: string, data: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
      lastSync.value = new Date();
    } catch (error) {
      console.error('Error saving cache for ' + key + ':', error);
    }
  };

  // Verificar si los datos están actualizados
  const checkDataFreshness = async (): Promise<boolean> => {
    try {
      const metadataDoc = await getDoc(doc(metadataCollection, 'last_update'));
      const metadata = metadataDoc.data();
      
      if (!metadata) return false;

      const lastServerUpdate = metadata.timestamp.toDate();
      const lastLocalSync = loadFromCache<string>(STORAGE_KEYS.LAST_SYNC);
      
      if (!lastLocalSync) return false;

      const lastSyncDate = new Date(lastLocalSync);
      isOutdated.value = lastServerUpdate > lastSyncDate;
      
      return !isOutdated.value;
    } catch (error) {
      console.error('Error checking data freshness:', error);
      return false;
    }
  };

  // Limpiar caché
  const clearCache = (): void => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    lastSync.value = null;
    isOutdated.value = false;
  };

  return {
    lastSync,
    isOutdated,
    loadFromCache,
    saveToCache,
    checkDataFreshness,
    clearCache
  };
});